/**
 * HISIP-005: Allowed Styles Panel Component
 * 
 * Multi-select control for choosing allowed visual styles
 */

import { motion, AnimatePresence } from 'framer-motion';
import { Check, Palette, Film, Megaphone, GraduationCap, Shapes } from 'lucide-react';
import React, { useLayoutEffect, useRef, useState } from 'react';

import type { TemplateReference } from '@/hooks/useTemplates';
import { cn } from '@/utils/cn';

// Holographic shimmer effect
const HolographicShimmer: React.FC<{ active: boolean }> = ({ active }) => (
  <motion.div
    className="absolute inset-0 pointer-events-none"
    initial={{ opacity: 0 }}
    animate={{ opacity: active ? 1 : 0 }}
    transition={{ duration: 0.3 }}
  >
    <motion.div
      className="absolute inset-0 bg-gradient-to-tr from-cyan-500/10 via-purple-500/10 to-pink-500/10 bg-[length:200%_200%] blur-[40px]"
      animate={{
        backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'],
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'linear'
      }}
    />
  </motion.div>
);

interface AllowedStylesPanelProps {
  availableStyles: TemplateReference[];
  selectedStyles: string[];
  onChange: (selectedSlugs: string[]) => void;
  collapsed?: boolean;
}

const AllowedStylesPanel: React.FC<AllowedStylesPanelProps> = ({
  availableStyles,
  selectedStyles,
  onChange,
  collapsed = false
}) => {
  const getStyleIcon = (slug: string) => {
    // Cinematic family
    if (slug === 'noir_contrast' || slug === 'golden_hour_film' || slug === 'anamorphic_depth' || slug === 'raw_authentic') return Film;
    // Advertiser family
    if (slug === 'studio_polish' || slug === 'product_hero' || slug === 'lifestyle_bright') return Megaphone;
    // Educational family
    if (slug === 'whiteboard_clean' || slug === 'infographic_flat' || slug === 'lab_demo' || slug === 'visceral_truth') return GraduationCap;
    // Animated family
    if (slug === 'toon_shaded' || slug === 'motion_graphics_bold' || slug === 'hand_drawn_grain' || slug === 'digital_rebellion') return Shapes;
    return Shapes;
  };
  const handleStyleToggle = (styleSlug: string) => {
    if (collapsed) return; // no interaction when collapsed
    const isSelected = selectedStyles.includes(styleSlug);
    
    if (isSelected) {
      onChange(selectedStyles.filter(slug => slug !== styleSlug));
    } else {
      onChange([...selectedStyles, styleSlug]);
    }
  };

  // (select all removed)

  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);
  
  // Measure height whenever collapse state flips using the hidden grid snapshot
  useLayoutEffect(() => {
    if (!contentRef.current) return;
    const h = contentRef.current.getBoundingClientRect().height;
    setHeight(h);
  }, [collapsed]);

  // Ensure initial measurement once templates are available to prevent zero-height on first paint
  useLayoutEffect(() => {
    if (!contentRef.current) return;
    const h = contentRef.current.getBoundingClientRect().height;
    if (h > 0) {
      setHeight(h);
    }
    // Intentionally exclude 'height' and 'collapsed' to avoid mid-animation recalculation during collapse
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableStyles.length]);

  if (availableStyles.length === 0) {
    return (
      <div className="text-center py-8">
        <Palette className="w-8 h-8 text-white/20 mx-auto mb-3" />
        <p className="text-white/40 font-mono text-sm">No styles available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Holographic Background Effect */}
      <HolographicShimmer active={selectedStyles.length > 0} />
      
      {/* Style Grid with synced height animation */}
      <motion.div 
        initial={false} 
        animate={{ height }} 
        transition={{ 
          duration: collapsed ? 0.9 : 0.7, // Container: 0.7s expand, 0.9s collapse
          ease: 'easeInOut'
        }} 
        className="overflow-hidden relative z-10"
      >
        {/* Hidden measurement grid (absolute, invisible, non-interactive) */}
        <div
          ref={contentRef}
          className="grid grid-cols-3 gap-4 invisible pointer-events-none absolute left-0 top-0 w-full"
          aria-hidden
        >
          {(collapsed
            ? availableStyles.filter(s => selectedStyles.includes(s.slug))
            : availableStyles
          ).map((style) => (
            <div key={`measure-${style.id}`} className="h-28 w-full" />
          ))}
        </div>

        {/* Visible grid with animated items */}
        <div className="grid grid-cols-3 gap-4 relative">
        <AnimatePresence mode="popLayout" initial={false}>
        {availableStyles.map((style) => {
          const isSelected = selectedStyles.includes(style.slug);
          const atLimit = !isSelected && selectedStyles.length >= 3;
          const shouldRender = !collapsed || isSelected;

          return (
            shouldRender && (
            <motion.div
              key={style.id}
              layout="position" // Only animate position, not size
              // Remove exiting items from layout immediately to avoid two-phase repositioning
              // @ts-expect-error presenceAffectsLayout is supported at runtime
              presenceAffectsLayout={false}
              initial={{ opacity: collapsed && !isSelected ? 0 : 1 }}
              animate={{ 
                opacity: collapsed && !isSelected ? 0 : 1,
                // Remove scale to prevent size changes
              }}
              // Force immediate removal from layout while fading to avoid snap-back
              exit={{ opacity: 0, position: 'absolute', transition: { duration: 0.3, ease: 'easeIn' } }}
              transition={{ 
                layout: {
                  duration: 0.8,
                  ease: [0.43, 0.13, 0.23, 0.96], // Smooth easing for position changes
                },
                opacity: {
                  duration: collapsed ? (isSelected ? 0.6 : 0.3) : 0.8,
                  delay: !collapsed && !isSelected ? 0.3 : 0,
                  ease: collapsed ? 'easeIn' : [0.12, 0, 0.39, 0]
                }
              }}
              onClick={() => { if (!atLimit && (!collapsed || isSelected)) handleStyleToggle(style.slug); }}
              className={cn(
                "relative p-4 rounded-lg border transition-all duration-300 group overflow-hidden",
                "min-w-0 flex-shrink-0 h-28 w-full",
                collapsed ? 'pointer-events-none' : 'cursor-pointer',
                isSelected
                  ? "bg-gradient-to-br from-cyan-500/30 via-purple-500/20 to-pink-500/20 border-cyan-400/60 shadow-2xl shadow-cyan-500/30"
                  : atLimit
                    ? "bg-black/20 border-white/10 opacity-40 cursor-not-allowed"
                    : "bg-black/40 border-white/20 hover:border-cyan-400/40 hover:bg-gradient-to-br hover:from-cyan-500/10 hover:to-purple-500/10"
              )}
            >
              {/* Non-animating content wrapper to preserve dimensions */}
              <div className="absolute inset-0 p-4">
                <div className="relative z-10 flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className={cn(
                      "font-mono text-sm transition-all duration-300 flex items-center gap-2",
                      isSelected ? "text-cyan-300 drop-shadow-[0_0_10px_rgba(6,182,212,0.5)]" : "text-white/90 group-hover:text-cyan-300"
                    )}>
                      <span className="whitespace-nowrap overflow-hidden text-ellipsis inline-block align-middle max-w-full">
                        {style.name}
                      </span>
                      {(() => { 
                        const Icon = getStyleIcon(style.slug); 
                        return <Icon className={cn(
                          "inline-block align-middle transition-all duration-300",
                          isSelected ? "text-cyan-300 drop-shadow-[0_0_8px_rgba(6,182,212,0.8)]" : "text-white/50 group-hover:text-cyan-300"
                        )} width={14} height={14} /> 
                      })()}
                    </h4>
                  </div>

                  <motion.div
                    initial={false}
                    animate={{ 
                      opacity: collapsed && !isSelected ? 0 : 1
                    }}
                    transition={{ 
                      duration: collapsed ? (isSelected ? 0.6 : 0.3) : 0.8,
                      delay: !collapsed && !isSelected ? 0.3 : 0, // Match card fade-in delay
                      ease: collapsed ? 'easeIn' : [0.12, 0, 0.39, 0]
                    }}
                    className={cn(
                      "relative w-5 h-5 rounded border-2 flex items-center justify-center transition-all",
                      isSelected
                        ? "bg-gradient-to-br from-cyan-400/50 to-purple-400/50 border-cyan-400 shadow-lg shadow-cyan-400/40"
                        : atLimit ? "border-white/10" : "border-white/30 group-hover:border-cyan-400/50"
                    )}
                  >
                    {isSelected && (
                      <Check className="w-3 h-3 text-cyan-300 drop-shadow-[0_0_4px_rgba(6,182,212,0.8)]" />
                    )}
                  </motion.div>
                </div>

                {/* Enhanced description */}
                <p className="relative z-10 text-xs text-white/60 font-mono leading-relaxed line-clamp-2 transition-all duration-300">
                  {style.description}
                </p>
              </div>
            </motion.div>
          ));
        })}
        </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default AllowedStylesPanel;